import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { X, BookOpen, ChevronRight } from "lucide-react";

import { ARTICLES } from "./help/content";

/**
 * HelpDrawer — full-height slide-in from the right that hosts the in-app docs.
 *
 *   TOC sidebar (left) lists every article.
 *   Article pane (right) renders the selected article from the structured
 *   blocks defined in /help/content.js.
 *
 * Triggered by the ? button in the TopBar and by `Shift + ?` from anywhere.
 *
 * Screenshots referenced by `img` blocks live under /app/frontend/public/help/
 * and are served as static assets at /help/*.jpg.
 */
export default function HelpDrawer({ open, onClose, initialArticleId }) {
  const [activeId, setActiveId] = useState(initialArticleId || ARTICLES[0].id);

  useEffect(() => {
    if (open) setActiveId(initialArticleId || ARTICLES[0].id);
  }, [open, initialArticleId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const active = ARTICLES.find((a) => a.id === activeId) || ARTICLES[0];

  return (
    <div
      className="fixed inset-0 z-[90] flex bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="help-overlay"
    >
      <div className="flex-1" />
      <div
        className="w-[900px] max-w-full h-full bg-bg-surface border-l border-line-strong shadow-[0_0_64px_rgba(0,0,0,0.7)] animate-slide-up flex"
        onClick={(e) => e.stopPropagation()}
        data-testid="help-drawer"
      >
        {/* TOC */}
        <aside className="w-60 border-r border-line bg-bg-base flex flex-col">
          <div className="px-3 py-3 border-b border-line-strong flex items-center gap-2">
            <BookOpen className="w-3.5 h-3.5 text-amber" />
            <div className="mono text-2xs uppercase tracking-[0.2em] text-ink-mute">HELP · v1</div>
          </div>
          <nav className="flex-1 overflow-auto py-2">
            {ARTICLES.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => setActiveId(a.id)}
                className={clsx(
                  "w-full text-left px-3 py-2 border-l-2 transition-colors group",
                  a.id === activeId
                    ? "border-amber bg-amber-glow text-ink"
                    : "border-transparent hover:border-line text-ink-dim hover:text-ink hover:bg-bg-elevated"
                )}
                data-testid={"help-toc-" + a.id}
              >
                <div className="text-xs leading-tight">{a.title}</div>
                <div className="text-2xs text-ink-mute leading-tight mt-0.5 truncate">{a.summary}</div>
              </button>
            ))}
          </nav>
          <div className="px-3 py-2 border-t border-line text-2xs mono text-ink-mute uppercase tracking-widest">
            Shift + ? to reopen
          </div>
        </aside>

        {/* Article */}
        <section className="flex-1 flex flex-col">
          <header className="flex items-center justify-between px-5 py-3 border-b border-line-strong bg-bg-elevated">
            <div className="min-w-0">
              <div className="mono text-2xs uppercase tracking-[0.2em] text-ink-mute">ARTICLE</div>
              <h1 className="text-lg text-ink mt-0.5 truncate">{active.title}</h1>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost"
              data-testid="help-close"
              title="Close (Esc)"
            >
              <X className="w-3 h-3" />
            </button>
          </header>
          <div
            className="flex-1 overflow-auto px-6 py-5 space-y-4"
            data-testid={"help-article-" + active.id}
          >
            {active.blocks.map((b, i) => (
              <ArticleBlock key={`${active.id}-${i}-${b.type}`} block={b} />
            ))}
            <ArticleNav activeId={activeId} onJump={setActiveId} />
          </div>
        </section>
      </div>
    </div>
  );
}

function ArticleBlock({ block }) {
  switch (block.type) {
    case "h2":
      return (
        <h2 className="mono text-2xs uppercase tracking-[0.25em] text-amber border-b border-line-subtle pb-1 pt-2">
          {block.text}
        </h2>
      );
    case "p":
      return <p className="text-sm text-ink leading-relaxed">{block.text}</p>;
    case "ul":
      return (
        <ul className="space-y-1.5 pl-1">
          {block.items.map((item, idx) => (
            <li key={`${idx}-${String(item).slice(0, 24)}`} className="text-sm text-ink flex gap-2 leading-relaxed">
              <span className="mono text-cyan shrink-0 mt-1">—</span>
              <span className="flex-1">{item}</span>
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className="space-y-1.5 pl-1">
          {block.items.map((item, idx) => (
            <li key={`${idx}-${String(item).slice(0, 24)}`} className="text-sm text-ink flex gap-2 leading-relaxed">
              <span className="mono text-amber shrink-0 mt-1 tabular-nums w-5 text-right">{idx + 1}.</span>
              <span className="flex-1">{item}</span>
            </li>
          ))}
        </ol>
      );
    case "kbd":
      return (
        <div className="flex items-center gap-3 py-1">
          <KeyCombo combo={block.combo} />
          <span className="text-sm text-ink-dim">{block.label}</span>
        </div>
      );
    case "img":
      return (
        <figure className="border border-line bg-bg-base mt-1 mb-1">
          <img
            src={block.src}
            alt={block.caption || "screenshot"}
            className="w-full block"
            loading="lazy"
          />
          {block.caption && (
            <figcaption className="text-2xs text-ink-mute leading-snug px-3 py-2 border-t border-line bg-bg-elevated">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );
    case "note":
      return (
        <div className="border-l-2 border-amber bg-amber-glow px-3 py-2">
          <div className="mono text-2xs uppercase tracking-[0.2em] text-amber mb-1">NOTE</div>
          <p className="text-sm text-ink leading-relaxed">{block.text}</p>
        </div>
      );
    case "code":
      return (
        <pre className="mono text-2xs text-ink bg-bg-base border border-line px-3 py-2 overflow-x-auto">
          {block.text}
        </pre>
      );
    default:
      return null;
  }
}

function KeyCombo({ combo }) {
  const keys = combo.split(" ").filter(Boolean);
  return (
    <div className="flex items-center gap-1 shrink-0">
      {keys.map((k, i) => (
        <kbd
          key={`${i}-${k}`}
          className="mono text-2xs uppercase tracking-widest bg-bg-elevated border border-line-strong px-2 py-0.5 text-ink"
        >
          {k}
        </kbd>
      ))}
    </div>
  );
}

function ArticleNav({ activeId, onJump }) {
  const idx = ARTICLES.findIndex((a) => a.id === activeId);
  const next = ARTICLES[idx + 1];
  if (!next) return null;
  return (
    <button
      type="button"
      onClick={() => onJump(next.id)}
      className="w-full mt-6 flex items-center justify-between px-4 py-3 border border-line bg-bg-elevated hover:border-amber hover:bg-amber-glow transition-colors group"
      data-testid={"help-next-" + next.id}
    >
      <div className="text-left">
        <div className="mono text-2xs uppercase tracking-[0.2em] text-ink-mute">UP NEXT</div>
        <div className="text-sm text-ink mt-0.5">{next.title}</div>
      </div>
      <ChevronRight className="w-4 h-4 text-ink-mute group-hover:text-amber" />
    </button>
  );
}
